import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { saveAuth } from '@/lib/auth';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useSEO } from '@/lib/seo';

// Message d'erreur générique — ne jamais préciser si l'email existe ou si c'est le mot de passe qui est faux
const GENERIC_LOGIN_ERROR = 'Identifiant ou mot de passe incorrect.';

export default function AdminLogin() {
  useSEO({ title: 'Connexion admin | Groupe Ambulances Beaulon' });
  const navigate = useNavigate();
  // Aucune valeur préremplie — pas de fuite d'identifiants
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Normalisation côté client (lowercase + trim) — la vraie normalisation reste côté serveur
      const normalizedEmail = email.trim().toLowerCase();
      const res = await api.post('/admin/login', { email: normalizedEmail, password });
      saveAuth(res.data.token, res.data.admin);
      toast.success('Connexion réussie');
      navigate('/admin');
    } catch (err) {
      // Rate-limit : afficher le délai d'attente si présent (429), sinon message générique unique
      const status = err?.response?.status;
      if (status === 429) {
        const retryAfter = err?.response?.data?.retry_after;
        toast.error(
          retryAfter
            ? `Trop de tentatives — réessayez dans ${retryAfter} secondes.`
            : 'Trop de tentatives — merci de patienter avant de réessayer.'
        );
      } else {
        toast.error(GENERIC_LOGIN_ERROR);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 via-white to-anthracite-100 px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-6">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-brand-600 to-brand-800 flex items-center justify-center">
            <ShieldCheck className="h-6 w-6 text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading font-semibold text-anthracite-900 text-sm">Groupe Ambulances</span>
            <span className="font-heading font-extrabold text-brand-800 text-base -mt-0.5">Beaulon</span>
          </div>
        </Link>

        <div className="card-base p-6 sm:p-8">
          <h1 className="h3 text-anthracite-950 text-center">Espace administrateur</h1>
          <p className="text-sm text-muted-foreground text-center mt-1">
            Connectez-vous pour accéder au tableau de bord
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4" autoComplete="off">
            <div>
              <Label htmlFor="admin-email">Adresse e-mail</Label>
              <Input
                id="admin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                spellCheck={false}
                className="mt-1.5"
                placeholder="votre.email@exemple.fr"
                data-testid="admin-login-email"
              />
            </div>

            <div>
              <Label htmlFor="admin-password">Mot de passe</Label>
              <div className="relative mt-1.5">
                <Input
                  id="admin-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="pr-10"
                  data-testid="admin-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center rounded-md text-anthracite-500 hover:text-anthracite-900 hover:bg-anthracite-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  data-testid="admin-login-toggle-password"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full"
              data-testid="admin-login-submit"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Connexion…</>
              ) : (
                'Se connecter'
              )}
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          <Link to="/" className="hover:text-anthracite-900">← Retour au site</Link>
        </p>
      </div>
    </div>
  );
}
